import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PiCaretDownLight,
  PiXLight,
} from 'react-icons/pi';
import apis from '../../../utils/apis';
import ProfileImage from '../../Header/ProfileImage';
import SearchInput from '../../shared/TailwindComponents/SearchInput';
import { FIELD_HELP_CLASS, FIELD_LABEL_CLASS } from '../../shared/TailwindComponents/FieldShell';
import { normalise } from '../../shared/access/accessModel';
import Spinner from '../../shared/TailwindComponents/Spinner';

interface Member {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  profile_picture?: string;
}

interface InlineAddMembersSectionProps {
  selectedMemberIds: string[];
  onMembersChange: (memberIds: string[]) => void;
  excludeIds?: string[];
  title?: string;
}

const InlineAddMembersSection: React.FC<InlineAddMembersSectionProps> = ({
  selectedMemberIds,
  onMembersChange,
  excludeIds = [],
  title = 'Team Members',
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Load available members
  const loadMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const response = await apis.get('/org-people/people');

      if (response?.data?.success && Array.isArray(response.data.people)) {
        const availableMembers = response.data.people.filter(
          (member: any) => !excludeIds.includes(member.id)
        );
        setMembers(availableMembers);
        setFilteredMembers(availableMembers);
      } else {
        console.warn('[WARNING] No members in response');
        setMembers([]);
        setFilteredMembers([]);
      }
    } catch (err: any) {
      console.error('[ERROR] Error loading members:', err);
      setMembers([]);
      setFilteredMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, [excludeIds]);

  // Load members when section expands
  useEffect(() => {
    if (isExpanded && members.length === 0) {
      loadMembers();
    }
  }, [isExpanded, members.length, loadMembers]);

  // Handle search
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setFilteredMembers(members);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = members.filter(
      (member) =>
        member.first_name.toLowerCase().includes(lowerQuery) ||
        member.last_name.toLowerCase().includes(lowerQuery) ||
        member.email.toLowerCase().includes(lowerQuery)
    );
    setFilteredMembers(filtered);
  }, [members]);

  const handleSelectMember = (memberId: string) => {
    if (!selectedMemberIds.includes(memberId)) {
      onMembersChange([...selectedMemberIds, memberId]);
    }
  };

  const handleRemoveMember = (memberId: string) => {
    onMembersChange(selectedMemberIds.filter((id) => id !== memberId));
  };

  // Get member details from loaded members
  const getMemberDetails = (memberId: string): Member | undefined => {
    return members.find((m) => m.id === memberId);
  };

  // Get all selected member details
  const selectedMembers = selectedMemberIds
    .map((id) => getMemberDetails(id))
    .filter(Boolean) as Member[];

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
      {/* Section Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between mb-4 group"
      >
        <span className={FIELD_LABEL_CLASS}>
          {t(title)} ({selectedMemberIds.length})
        </span>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <PiCaretDownLight className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
        </motion.div>
      </button>

      {/* Collapsible Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4 overflow-hidden"
          >
            {/* Search Box */}
            <div className="p-1">
              <SearchInput
                value={searchQuery}
                onChange={handleSearch}
                placeholder={t('Search name or email...')}
              />
            </div>

            {/* Available Members List */}
            <div className="max-h-[calc(100vh-300px)] overflow-y-auto space-y-2 custom-scrollbar rounded-lg bg-gray-50 dark:bg-gray-800 p-2">
              {loadingMembers ? (
                <div className="text-center py-8">
                  <Spinner size="glyph" className="!h-6 !w-6 inline-block" />
                </div>
              ) : filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <motion.button
                    key={member.id}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    type="button"
                    onClick={() => handleSelectMember(member.id)}
                    disabled={selectedMemberIds.includes(member.id)}
                    className="w-full p-3 text-start hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3">
                      <ProfileImage
                        profilePicture={normalise(member).avatar}
                        email={member.email}
                        initials={`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()}
                        size="w-10 h-10"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {member.email}
                        </p>
                      </div>
                    </div>
                  </motion.button>
                ))
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  {searchQuery ? t('No members found') : t('No available members')}
                </div>
              )}
            </div>

            {/* Selected Members */}
            {selectedMembers.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <p className={`${FIELD_HELP_CLASS} mb-2`}>
                  {t('Selected')}
                </p>
                <div className="space-y-2">
                  {selectedMembers.map((member) => (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="flex items-center justify-between p-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <ProfileImage
                          profilePicture={normalise(member).avatar}
                          email={member.email}
                          initials={`${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()}
                          size="w-8 h-8"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {member.first_name} {member.last_name}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="flex-shrink-0 ms-2 p-1 text-gray-400 hover:text-danger-600 dark:hover:text-danger-400 transition-colors rounded hover:bg-danger-50 dark:hover:bg-danger-900/10"
                      >
                        <PiXLight className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InlineAddMembersSection;
